import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import Loader from '../../../components/Loader'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import CompanySelect from '../../../components/v2/CompanySelect'
import HorizontalCard from '../../../components/v2/HorizontalCard'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import WelcomerProgressbar from '../../../components/v2/progrees-bars/WelcomerProgressbar'
import { useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import {
  WelcomerState,
  clearAddon,
  clearGroupAddon,
  setChooseMethod,
  setCompanyId,
  setFixedOffer,
  setLeftCompanyIds,
  setOffer,
  setPackageIds,
} from '../../../store/welcomerSlice'

const WelcomerChooseMethod = () => {
  //
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const { t } = useTranslation()
  //
  const navigate = useNavigate()
  //
  const dispatch = useDispatch()
  //
  const { filter, screenHeader } = useAppSelector((data: { welcomer: WelcomerState }) => data.welcomer)
  //
  const { data: welcomerChoice, isLoading: isWelcomerChoiceLoading } =
    api.useGetWelcomerPagesInfoQuery('welcomer-frani-or-myself')
  //
  const pageTitle = welcomerChoice?.translations?.[lang]?.title
  const stepName = welcomerChoice?.translations?.[lang]?.stepName
  const listItems = welcomerChoice?.listItems?.slice().sort((a, b) => a.sort - b.sort)
  const isDataValid = (data: any) => {
    if (!Array.isArray(data)) {
      return false
    }

    for (let i = 0; i < data.length; i++) {
      const item = data[i]
      if (
        item.age === null ||
        item.age === 0 ||
        item.date === null ||
        item.date.length !== 14 ||
        Number(item.age) < 0
      ) {
        return false
      }
    }

    return true
  }
  //
  const [getLeftCompanies, { data: leftCompanies, isLoading: isLeftCompanyLoading }] =
    api.useWelcomerLeftCompaniesLastStepMutation()
  //
  useEffect(() => {
    if (
      filter.productCategory &&
      filter.welcomersDate &&
      filter.welcomersDate !== undefined &&
      filter.welcomersDate.length >= 1 &&
      isDataValid(filter.welcomersDate) &&
      filter.currency &&
      filter.singleDates?.start &&
      filter?.singleDates?.end
    ) {
      const GetCompaniesFunction = async () => {
        try {
          //
          await getLeftCompanies({
            step: 'step-three',
            productCategory: filter.productCategory,
            startDate: filter.singleDates?.start as string,
            endDate: filter.singleDates?.end as string,
            currency: filter.currency,
            limit: filter.limit as number,
            // @ts-ignore
            ages: filter.welcomersDate.map((t) => (t.month === null ? t.age : 0)),
          }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (GetCompaniesFunction) (ChooseMethod)')
          }
        }
      }
      //
      GetCompaniesFunction()
    }

    return () => {}
  }, [
    filter.productCategory,
    filter.singleDates?.start,
    filter.singleDates?.end,
    filter.currency,
    filter.limit,
    filter.welcomersDate,
    getLeftCompanies,
  ])
  //
  //
  const SelectedCompanyArray: WelcomerState['filter']['selectedCompaniesIds'] = filter.selectedCompaniesIds
  //
  // selected companies
  const matchingObjects = leftCompanies?.uniqueCompanies?.filter((obj) =>
    SelectedCompanyArray?.includes(obj.id as never),
  )
  //
  useEffect(() => {
    const LeftCompanyIds = leftCompanies?.uniqueCompanies?.map((x) => x.id)
    const LeftPackageIds = leftCompanies?.packageData?.map((x) => x.id)
    //
    if (filter.chooseMethod === 'frani-choose' && LeftCompanyIds) {
      dispatch(setLeftCompanyIds(LeftCompanyIds))
    }

    if (leftCompanies && leftCompanies.packageData) {
      dispatch(setPackageIds(LeftPackageIds))
    }

    return () => {}
  }, [dispatch, filter.chooseMethod, leftCompanies])
  //
  // auto navigate first page
  useEffect(() => {
    if (!filter.singleDates?.start) {
      navigate('/welcomer-start-date')
    }
    //
    return () => {}
  }, [filter.singleDates?.start, navigate])
  //
  //
  const MainLoading = isLeftCompanyLoading || isWelcomerChoiceLoading

  return MainLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageTitle} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <ScreenHeader totalCount={screenHeader.totalCount} passedCount={screenHeader.passedCount} />
      <Content>
        <Title text={pageTitle} />
        <WelcomerProgressbar stepName={stepName} />

        <Block>
          {listItems &&
            listItems.map((item, index) => {
              return (
                <HorizontalCard
                  className="horizontal-card"
                  key={index}
                  title={item?.translations?.[lang]?.title}
                  description={item?.translations?.[lang]?.body}
                  imgUrl={item?.image}
                  selected={item.type === filter.chooseMethod ? true : false}
                  onClick={() => [
                    dispatch(setChooseMethod(item.type)),
                    dispatch(clearAddon([])),
                    dispatch(clearGroupAddon([])),
                    dispatch(setOffer(undefined)),
                  ]}
                >
                  {item.type === filter.chooseMethod && filter.chooseMethod === 'my-choose' ? (
                    <Inner>
                      <hr />
                      <CompanyTitle>{t('chooseInsuranceCompany')}</CompanyTitle>
                      <CompanyDescription>{t('chooseInsuranceCompanyRules')}</CompanyDescription>

                      <CompanySelect
                        closeMenuOnSelect={false}
                        onChange={(data) => dispatch(setCompanyId(data.map((item: any) => item.value)))}
                        isMulti={true}
                        maxSelectLenght={3}
                        maxSelectText={`${t('companyCmpare')}`}
                        placeholder={t('selectCompanies')}
                        showOptionCheckbox={true}
                        value={matchingObjects?.map((s) => ({
                          label: s.brandName?.[lang]?.title,
                          value: s.id,
                          imgUrl: s.logo,
                        }))}
                        options={
                          leftCompanies?.uniqueCompanies?.map((item) => ({
                            value: item.id,
                            label: item.brandName?.[lang]?.title,
                            imgUrl: item.logo,
                          })) || []
                        }
                      />
                    </Inner>
                  ) : null}
                </HorizontalCard>
              )
            })}
        </Block>
        <Buttons>
          <NavigationButton
            text={`${t('back')}`}
            buttonType="prev"
            onClick={() => navigate('/welcomer-add-travelers')}
          />
          <NavigationButton
            text={`${t('next')}`}
            buttonType="next"
            disabled={
              filter.productCategory &&
              filter.chooseMethod &&
              ((filter.chooseMethod === 'frani-choose' && filter.leftCompanyIds && filter.leftCompanyIds.length >= 1) ||
                (filter.chooseMethod === 'my-choose' &&
                  filter.selectedCompaniesIds &&
                  filter.selectedCompaniesIds.length >= 1)) &&
              filter.currency &&
              filter.limit &&
              filter.welcomersDate &&
              filter.welcomersDate.find((x) => x.age !== 0) &&
              filter.welcomersDate.find((x) => x.date !== null) &&
              filter.singleDates?.start &&
              filter.singleDates?.end
                ? false
                : true
            }
            onClick={() => {
              if (filter.chooseMethod === 'frani-choose') {
                navigate('/welcomer-offer')
                dispatch(setFixedOffer(false))
              }
              //
              if (filter.chooseMethod === 'my-choose') {
                navigate('/welcomer-offers')
                dispatch(setFixedOffer(false))
              }
            }}
          />
        </Buttons>
      </Content>
      <BackgroundIcon />
    </Wrapper>
  )
}
//
//
// styles
const CompanyTitle = styled.p`
  font-size: 18px;
  color: ${Colors.primaryBlack};
  margin-bottom: 1px;
`
const CompanyDescription = styled.p`
  font-size: 14px;
  color: ${Colors.nevadaGrey};
  margin-bottom: 18px;
`
const Inner = styled.div`
  /*  */
`
const Block = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  margin-inline: auto;
  max-width: 464px;
  width: 100%;

  gap: 24px;
  ${Responsive.mobile} {
    margin-top: 24px;
    gap: 20px;
  }
`
const Buttons = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 48px;
  width: min-content;
  margin-inline: auto;
  button {
    width: 175px;
  }

  ${Responsive.mobile} {
    gap: 16px;
    button {
      width: 140px;
    }
  }
`
const Content = styled.div`
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;

  ${Responsive.mobile} {
    margin-top: 26px;
  }
`
const Wrapper = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  min-height: 100vh;
  /*  */
`
export default WelcomerChooseMethod
